import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable, applyColumnFilters } from "../../components";
import { useAuth } from "../../contexts/auth";
import { baseAxios, Context } from "../../utils/config";
import { ROLE_SALON_ADMIN, ROLE_SYSTEM_ADMIN } from "../../utils/constants";
import { colors } from "../../theme/colors";

const SAVE_URL = "gateway/dh6zwk9ifl";

const EDITABLE_FIELDS = [
  "inventory_current_quantity",
  "inventory_desired_quantity",
  "inventory_product_price",
];

function rowKey(row) {
  return `${row.inventory_location_id}-${row.inventory_product_id}`;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round2(value) {
  return Math.round(toNumber(value) * 100) / 100;
}

/** Arrow keys / spinner should add or subtract 1 without snapping 5.46 → 6. */
function stepByWholeNumber(value, delta) {
  return Math.max(0, round2(toNumber(value) + delta));
}

function formatQty(value) {
  return round2(value).toFixed(2);
}

function formatMoney(value) {
  const n = round2(value);
  return `$${n < 0 ? "0.00" : n.toFixed(2)}`;
}

function formatLastOrdered(value) {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid() || d.year() < 2000) return "—";
  return d.format("M/D/YY h:mm A");
}

function deriveFields(row) {
  const current = round2(row.inventory_current_quantity);
  const desired = round2(row.inventory_desired_quantity);
  const price = round2(row.inventory_product_price);
  return {
    ...row,
    inventory_current_quantity: current,
    inventory_desired_quantity: desired,
    inventory_product_price: price,
    inventory_on_hand: round2(current * price),
    inventory_quantity_needed: Math.max(0, round2(desired - current)),
  };
}

function normalizeInventory(items) {
  return (items || []).map((item) => deriveFields(item));
}

function mapApiError(error) {
  if (error.response?.status === 403) return "Not allowed to see inventory data";
  if (error.response?.status === 404) return "Inventory endpoint not found";
  return error.response?.data?.message || "Error loading inventory data";
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (field === "inventory_last_changed_timestamp") {
    return dayjs(va).valueOf() - dayjs(vb).valueOf();
  }
  if (
    typeof va === "number" ||
    typeof vb === "number" ||
    EDITABLE_FIELDS.includes(field) ||
    field === "inventory_on_hand" ||
    field === "inventory_quantity_needed"
  ) {
    return toNumber(va) - toNumber(vb);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

const editFieldSx = {
  width: "100%",
  maxWidth: 112,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
    fontSize: 13,
    fontWeight: 600,
    minWidth: 0,
  },
  "& .MuiOutlinedInput-input": {
    py: 0.75,
    px: 1,
    minWidth: 0,
  },
};

/**
 * Current Inventory — full list with editable counts / unit price
 */
export default function Inventory(props) {
  const { location_name: filterLocationName } = props?.location?.state || {};
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const showProductPriceColumn = user.userRoles.some(
    (x) => x === ROLE_SYSTEM_ADMIN || x === ROLE_SALON_ADMIN
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState("product_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [pendingByKey, setPendingByKey] = useState({});

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const result = await baseAxios.get("inventory/current");
      const inventory = normalizeInventory(result.data?.inventory);
      setRows(inventory);
      setPendingByKey({});
    } catch (error) {
      setRows([]);
      toast.error(mapApiError(error), { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory, globalLocation?.location_id]);

  const displayRows = useMemo(() => {
    return rows.map((row) => {
      const pending = pendingByKey[rowKey(row)];
      if (!pending) return row;
      return deriveFields({ ...row, ...pending });
    });
  }, [rows, pendingByKey]);

  const columnFilterConfig = useMemo(
    () => ({
      inventory_current_quantity: { filterType: "number" },
      inventory_desired_quantity: { filterType: "number" },
      inventory_quantity_needed: { filterType: "number" },
      inventory_on_hand: { filterType: "number" },
      inventory_product_price: { filterType: "number" },
      inventory_last_changed_timestamp: {
        getFilterText: (row) =>
          formatLastOrdered(row.inventory_last_changed_timestamp),
      },
    }),
    []
  );

  const filteredRows = useMemo(() => {
    let     list = displayRows;
    if (filterLocationName) {
      list = list.filter(
        (row) =>
          String(row.location_name || "").toLowerCase() ===
          String(filterLocationName).toLowerCase()
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const haystack = [
        row.location_name,
        row.product_name,
        row.manufacturer_name,
        row.brand_name,
        row.category_name,
        row.inventory_notes,
        row.product_upc_code,
        formatQty(row.inventory_current_quantity),
        formatQty(row.inventory_desired_quantity),
        formatQty(row.inventory_quantity_needed),
        formatMoney(row.inventory_on_hand),
        formatMoney(row.inventory_product_price),
        formatLastOrdered(row.inventory_last_changed_timestamp),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [displayRows, filterLocationName, search]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  const pendingCount = useMemo(
    () => Object.keys(pendingByKey).length,
    [pendingByKey]
  );

  const handleFieldChange = useCallback((row, field, rawValue) => {
    const key = rowKey(row);
    const numeric = rawValue === "" || rawValue == null ? "" : Number(rawValue);
    setPendingByKey((prev) => {
      const original = rows.find((r) => rowKey(r) === key);
      const existing = prev[key] || {};
      const nextData = { ...existing, [field]: numeric };

      // Drop fields that match the original loaded value
      if (original && Number(original[field]) === Number(numeric)) {
        delete nextData[field];
      }

      if (!Object.keys(nextData).length) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: nextData };
    });
  }, [rows]);

  const handleRevert = useCallback(() => {
    setPendingByKey({});
  }, []);

  const handleSave = useCallback(async () => {
    const keys = Object.keys(pendingByKey);
    if (!keys.length) {
      toast.error("No changes to save", { duration: 2500 });
      return;
    }

    const changes = keys.map((key) => {
      const [inventory_location_id, inventory_product_id] = key
        .split("-")
        .map(Number);
      const data = {};
      Object.entries(pendingByKey[key]).forEach(([field, value]) => {
        if (value === "" || value == null || Number.isNaN(Number(value))) return;
        data[field] = Number(value);
      });
      return {
        type: "update",
        key: { inventory_location_id, inventory_product_id },
        data,
      };
    }).filter((c) => Object.keys(c.data).length > 0);

    if (!changes.length) {
      toast.error("No valid changes to save", { duration: 2500 });
      return;
    }

    setSaving(true);
    try {
      await baseAxios.post(SAVE_URL, { changes });
      toast.success("Inventory updated", { duration: 2500 });
      await loadInventory();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save inventory changes",
        { duration: 3000 }
      );
    } finally {
      setSaving(false);
    }
  }, [pendingByKey, loadInventory]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleColumnFilterChange = useCallback((field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    const exportRows = applyColumnFilters(
      sortedRows,
      columnFilters,
      columnFilterConfig
    );
    if (!exportRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Inventory");
      const columns = [
        { header: "Location", key: "location_name", width: 18 },
        { header: "Item", key: "product_name", width: 28 },
        { header: "Manufacturer", key: "manufacturer_name", width: 18 },
        { header: "Brand", key: "brand_name", width: 16 },
        { header: "Category", key: "category_name", width: 16 },
        { header: "Item Count", key: "inventory_current_quantity", width: 12 },
        { header: "Max Count", key: "inventory_desired_quantity", width: 12 },
        { header: "Order Quantity", key: "inventory_quantity_needed", width: 14 },
        { header: "Value on Hand", key: "inventory_on_hand", width: 14 },
      ];
      if (showProductPriceColumn) {
        columns.push({
          header: "Unit Price",
          key: "inventory_product_price",
          width: 12,
        });
      }
      columns.push(
        { header: "Last Ordered", key: "inventory_last_changed_timestamp", width: 18 },
        { header: "Notes", key: "inventory_notes", width: 24 },
        { header: "Barcode", key: "product_upc_code", width: 16 }
      );
      worksheet.columns = columns;
      worksheet.getRow(1).font = { bold: true };

      exportRows.forEach((row) => {
        worksheet.addRow({
          location_name: row.location_name || "",
          product_name: row.product_name || "",
          manufacturer_name: row.manufacturer_name || "",
          brand_name: row.brand_name || "",
          category_name: row.category_name || "",
          inventory_current_quantity: round2(row.inventory_current_quantity),
          inventory_desired_quantity: round2(row.inventory_desired_quantity),
          inventory_quantity_needed: round2(row.inventory_quantity_needed),
          inventory_on_hand: round2(row.inventory_on_hand),
          inventory_product_price: round2(row.inventory_product_price),
          inventory_last_changed_timestamp: formatLastOrdered(
            row.inventory_last_changed_timestamp
          ),
          inventory_notes: row.inventory_notes || "",
          product_upc_code: row.product_upc_code || "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Inventory_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows, showProductPriceColumn, columnFilters, columnFilterConfig]);

  const renderEditable = useCallback(
    (field, row, formatFn) => {
      const pending = pendingByKey[rowKey(row)];
      const hasEdit = pending && Object.prototype.hasOwnProperty.call(pending, field);
      const displayValue = hasEdit
        ? pending[field]
        : round2(row[field]);
      const isItemCount = field === "inventory_current_quantity";
      return (
        <TextField
          size="small"
          type="number"
          value={displayValue === "" ? "" : displayValue}
          onChange={(e) => handleFieldChange(row, field, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (!isItemCount) return;
            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
            e.preventDefault();
            e.stopPropagation();
            const delta = e.key === "ArrowUp" ? 1 : -1;
            handleFieldChange(row, field, stepByWholeNumber(e.target.value, delta));
          }}
          inputProps={{
            // "any" increments by 1 from the current value (5.46 → 6.46).
            // step="1" would snap to the next integer (5.46 → 6).
            step: isItemCount ? "any" : "0.01",
            min: "0",
          }}
          sx={{
            ...editFieldSx,
            "& .MuiOutlinedInput-root": {
              ...editFieldSx["& .MuiOutlinedInput-root"],
              ...(hasEdit
                ? {
                    borderColor: colors.gold,
                    "& fieldset": { borderColor: colors.gold },
                  }
                : {}),
            },
          }}
          title={formatFn ? formatFn(displayValue) : undefined}
        />
      );
    },
    [pendingByKey, handleFieldChange]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: "location_name",
        label: "Location",
        field: "location_name",
        width: "8%",
        render: (v) => v || "—",
      },
      {
        id: "product_name",
        label: "Item",
        field: "product_name",
        width: showProductPriceColumn ? "11%" : "16%",
        render: (v) => v || "—",
      },
      {
        id: "manufacturer_name",
        label: "Manufacturer",
        field: "manufacturer_name",
        width: "8%",
        render: (v) => v || "—",
      },
      {
        id: "brand_name",
        label: "Brand",
        field: "brand_name",
        width: "7%",
        render: (v) => v || "—",
      },
      {
        id: "category_name",
        label: "Category",
        field: "category_name",
        width: "7%",
        render: (v) => v || "—",
      },
      {
        id: "inventory_current_quantity",
        label: "Item Count",
        field: "inventory_current_quantity",
        filterType: "number",
        width: "8%",
        render: (_, row) => renderEditable("inventory_current_quantity", row),
      },
      {
        id: "inventory_desired_quantity",
        label: "Max Count",
        field: "inventory_desired_quantity",
        filterType: "number",
        width: "8%",
        render: (_, row) => renderEditable("inventory_desired_quantity", row),
      },
      {
        id: "inventory_quantity_needed",
        label: "Order Qty",
        field: "inventory_quantity_needed",
        filterType: "number",
        width: "7%",
        render: (v) => formatQty(v),
      },
      {
        id: "inventory_on_hand",
        label: "On Hand",
        field: "inventory_on_hand",
        filterType: "number",
        width: "7%",
        render: (v) => (
          <Typography component="span" fontWeight={600} fontSize={14}>
            {formatMoney(v)}
          </Typography>
        ),
      },
    ];

    if (showProductPriceColumn) {
      cols.push({
        id: "inventory_product_price",
        label: "Unit Price",
        field: "inventory_product_price",
        filterType: "number",
        width: "8%",
        render: (_, row) => renderEditable("inventory_product_price", row),
      });
    }

    cols.push(
      {
        id: "inventory_last_changed_timestamp",
        label: "Last Ordered",
        field: "inventory_last_changed_timestamp",
        getFilterText: (row) =>
          formatLastOrdered(row.inventory_last_changed_timestamp),
        width: "8%",
        render: (v) => formatLastOrdered(v),
      },
      {
        id: "inventory_notes",
        label: "Notes",
        field: "inventory_notes",
        width: "7%",
        render: (v) => v || "—",
      },
      {
        id: "product_upc_code",
        label: "Barcode",
        field: "product_upc_code",
        width: "6%",
        render: (v) => v || "—",
      }
    );

    return cols;
  }, [showProductPriceColumn, renderEditable]);

  return (
    <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Current Inventory
            </Typography>
            {filterLocationName ? (
              <Chip
                size="small"
                label={filterLocationName}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : globalLocation?.location_name ? (
              <Chip
                size="small"
                label={globalLocation.location_name}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : null}
            {pendingCount > 0 ? (
              <Chip
                size="small"
                label={`${pendingCount} unsaved`}
                sx={{
                  bgcolor: colors.gold,
                  color: colors.black,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Review stock levels and update item counts, max counts, and unit
            price.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search item, brand, barcode…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            aria-label="Refresh inventory"
            onClick={loadInventory}
            disabled={loading || saving}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<UndoIcon />}
            onClick={handleRevert}
            disabled={saving || pendingCount === 0}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Revert
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading || saving || pendingCount === 0}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !sortedRows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="product_name"
        initialOrder="asc"
        searchQuery={`${search}|${JSON.stringify(columnFilters)}|${filterLocationName ?? ""}|${globalLocation?.location_id ?? "all"}|${rows.length}`}
        clientSidePagination
        showFilterRow
        columnFilters={columnFilters}
        columnFilterConfig={columnFilterConfig}
        onColumnFilterChange={handleColumnFilterChange}
        skeletonHeight="40px"
        getRowKey={(row) => rowKey(row)}
        fitWidth
        emptyMessage="No inventory items found"
      />
    </Box>
  );
}
